import { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { policystreamStore, StoreState } from '../store';
import { FINAL_FORM } from '../queries';
import { useLazyQuery } from '@apollo/client';
import { Language } from '../Typescript/classes';
import { ProgramBuilderForm } from '../components/FormManager/classes';
import { useInPolicystream } from '../components/PolicyStream/InPolicyStream';
import { QuoteWizardQuestionInstance } from '../components/QuoteWizard/classes';

type UseEnsureFormPresentParams = null | {
  internalName?: string;
};
/**
 * Ensure that a form is present by checking that it's in the form store, and if not, fetching it from the backend
 */
export const useEnsureFormPresent = (
  params: UseEnsureFormPresentParams = {},
) => {
  const isPolicyStream = useInPolicystream();
  const [internalName, setInternalName] = useState<string | null>(null);
  const getForm = useCallback(
    (store: StoreState) => {
      const found = store.policystreamStore.forms.find(
        (form) => form.internalName === internalName,
      );
      if (found && process.env.NODE_ENV === 'development') {
        return found.copyWithAmendments({
          questionInstances: setNotRquired(found.questionInstances),
        });
      }
      return found;
    },
    [internalName],
  );
  const formInStore = useSelector(getForm);
  const dispatch = useDispatch();
  const [getFinalForm, { loading, data }] = useLazyQuery(FINAL_FORM);

  // set the internal name from the params, if possible
  useEffect(() => {
    if (params?.internalName) {
      setInternalName(params.internalName);
    }
  }, [params]);

  // get the form from the backend if we don't find it in the store
  useEffect(() => {
    if (!loading && !formInStore && internalName) {
      getFinalForm({
        variables: {
          internalName,
          isPolicystream: isPolicyStream,
        },
      });
    }
  }, [internalName, formInStore, loading, isPolicyStream]);

  // if we're getting it from the backend, put it in state once we have it
  useEffect(() => {
    if (!formInStore && data?.finalForm) {
      const newForm = ProgramBuilderForm.generateFromBackendResponse(
        data?.finalForm,
      ).toQuoteWizardForm(Language.english);
      dispatch(policystreamStore.actions.addForms({ forms: newForm }));
    }
  }, [data, formInStore]);

  return formInStore;
};

const setNotRquired = (
  questions: Array<QuoteWizardQuestionInstance>,
): Array<QuoteWizardQuestionInstance> => {
  return questions.map((q) =>
    q.copyWithAmendments({
      required: false,
      subQuestions: setNotRquired(q.subQuestions),
    }),
  );
};
